<script setup lang="ts">
/**
 * CoverIntroMockup — 视频开头遮挡面板 mockup
 * v8 真实能力（cover-intro.js）：视频开头 N 秒在播放器底部显示「比分遮挡面板」
 * （logo + 品牌 + 标题 + 进度条），盖住可能出现的比分，N 秒后自动关闭。
 * 面板只遮挡视觉，不拦截播放交互。
 */
import { ref, computed } from 'vue'

const seconds = ref(5)
const covered = ref(true)
const totalSeconds = 90

function formatTime(s: number): string {
  const m = Math.floor(s / 60)
  const sec = s % 60
  return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`
}

const sliderPercent = computed(() => (seconds.value / totalSeconds) * 100)

function toggleCover() {
  covered.value = !covered.value
}
</script>

<template>
  <div class="ci-mock">
    <div class="ci-mock__header">
      <span class="ci-mock__label">开头遮挡面板</span>
      <button type="button" class="ci-mock__toggle yfb-press" @click="toggleCover">
        {{ covered ? '遮挡中 · N 秒' : '未遮挡' }}
      </button>
    </div>
    <div class="ci-mock__player">
      <div class="ci-mock__video">
        <!-- 播放器画面（被遮挡的内容：比分在底部区域） -->
        <div class="ci-mock__frame">
          <div class="ci-mock__time">{{ formatTime(seconds) }} / {{ formatTime(totalSeconds) }}</div>
          <!-- 比分（会被面板盖住的剧透元素，放在画面下方被面板覆盖） -->
          <div class="ci-mock__score">
            <span class="ci-mock__team">阿根廷</span>
            <span class="ci-mock__score-num">2 : 1</span>
            <span class="ci-mock__team">法国</span>
          </div>

          <!-- 遮挡面板：底部区域，盖住下方的比分 -->
          <div v-if="covered" class="ci-mock__panel">
            <div class="ci-mock__panel-row">
              <span class="ci-mock__panel-dot" />
              <span class="ci-mock__panel-title">比分遮挡面板</span>
            </div>
            <span class="ci-mock__panel-desc">遮挡画面中可能出现的比分</span>
            <span class="ci-mock__panel-hint">{{ seconds }} 秒后自动关闭 · 也可手动关闭</span>
            <div class="ci-mock__panel-progress">
              <div class="ci-mock__panel-progress-fill"></div>
            </div>
          </div>
        </div>

        <!-- 时长设置滑杆 -->
        <div class="ci-mock__controls">
          <span class="ci-mock__ctrl-label">遮挡时长</span>
          <input
            type="range"
            min="1"
            max="10"
            step="1"
            :value="seconds"
            class="ci-mock__slider"
            aria-label="设置开头遮挡秒数"
            @input="(e) => (seconds = Number((e.target as HTMLInputElement).value))"
          />
          <span class="ci-mock__ctrl-value">{{ seconds }} 秒</span>
        </div>
        <div class="ci-mock__hint">开头 {{ seconds }} 秒盖住比分，N 秒后自动关闭</div>
      </div>
    </div>
    <div class="ci-mock__footer">比分不剧透 · 开场不被破坏</div>
  </div>
</template>

<style scoped>
.ci-mock {
  border: var(--yfb-border-2) solid var(--yfb-rule);
  background-color: var(--yfb-surface);
  box-shadow: var(--yfb-shadow-2);
  border-radius: var(--yfb-radius-lg);
  overflow: hidden;
}

.ci-mock__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--yfb-sp-3) var(--yfb-sp-4);
  background-color: var(--yfb-surface-2);
  color: var(--yfb-ink);
  border-bottom: 1px solid var(--yfb-rule-soft);
}

.ci-mock__label {
  font-family: var(--yfb-font-heading);
  font-size: 10px;
  font-weight: 700;
  color: var(--yfb-accent);
  text-transform: uppercase;
  letter-spacing: var(--yfb-ls-eyebrow);
}

.ci-mock__toggle {
  padding: 4px 12px;
  background-color: var(--yfb-accent);
  color: var(--yfb-on-accent);
  border: none;
  border-radius: var(--yfb-radius-sm);
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  cursor: pointer;
  font-family: inherit;
}

.ci-mock__toggle:hover {
  box-shadow: var(--yfb-shadow-hit);
}

.ci-mock__player {
  padding: var(--yfb-sp-4);
}

.ci-mock__video {
  background-color: var(--yfb-bg);
  padding: var(--yfb-sp-4);
  display: flex;
  flex-direction: column;
  gap: var(--yfb-sp-3);
  border-radius: var(--yfb-radius-md);
  box-shadow: inset 0 0 0 1px var(--yfb-rule-soft);
}

.ci-mock__frame {
  position: relative;
  aspect-ratio: 16 / 9;
  background-color: var(--yfb-panel-bg);
  border: var(--yfb-border-2) solid var(--yfb-rule-soft);
  border-radius: var(--yfb-radius-md);
  overflow: hidden;
}

.ci-mock__frame::before {
  content: "";
  position: absolute;
  inset: 0;
  background-image:
    repeating-linear-gradient(0deg, transparent 0, transparent 14px, var(--yfb-grid-line) 14px, var(--yfb-grid-line) 15px),
    repeating-linear-gradient(90deg, transparent 0, transparent 14px, var(--yfb-grid-line) 14px, var(--yfb-grid-line) 15px);
}

.ci-mock__time {
  position: absolute;
  top: var(--yfb-sp-2);
  left: var(--yfb-sp-2);
  z-index: 1;
  font-family: var(--yfb-mono);
  font-size: 10px;
  font-weight: 700;
  color: var(--yfb-accent);
  background: rgba(11, 11, 16, 0.7);
  padding: 2px 6px;
  border-radius: var(--yfb-radius-sm);
  letter-spacing: var(--yfb-ls-mono);
}

/* 比分（会被面板盖住）：放在画面下方中下部，正落在遮挡面板覆盖区域内 */
.ci-mock__score {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 12%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--yfb-sp-4);
  z-index: 1;
}

.ci-mock__team {
  font-family: var(--yfb-font-heading);
  font-size: var(--yfb-fs-md);
  font-weight: 700;
  color: var(--yfb-ink);
  background: var(--yfb-surface);
  padding: var(--yfb-sp-1) var(--yfb-sp-2);
  border: 1.5px dashed var(--yfb-rule-soft);
  border-radius: var(--yfb-radius-sm);
}

.ci-mock__score-num {
  font-family: var(--yfb-mono);
  font-size: var(--yfb-fs-xl);
  font-weight: 700;
  color: var(--yfb-ink);
  letter-spacing: var(--yfb-ls-eyebrow);
}

/* 遮挡面板：深黑盖住底部区域（向上盖过比分所在的中下部） */
.ci-mock__panel {
  position: absolute;
  left: 10%;
  right: 10%;
  bottom: 6%;
  top: 62%;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: var(--yfb-sp-3);
  background: rgba(0, 0, 0, 0.96);
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: var(--yfb-radius-md);
}

.ci-mock__panel-row {
  display: flex;
  align-items: center;
  gap: 6px;
}

.ci-mock__panel-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--yfb-accent);
  box-shadow: 0 0 6px var(--yfb-glow);
}

.ci-mock__panel-title {
  font-size: 13px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.95);
  letter-spacing: 0.01em;
}

.ci-mock__panel-desc {
  font-size: 10px;
  color: rgba(255, 255, 255, 0.8);
}

.ci-mock__panel-hint {
  font-size: 9px;
  color: rgba(255, 255, 255, 0.55);
}

.ci-mock__panel-progress {
  width: 100%;
  height: 4px;
  margin-top: 4px;
  background: rgba(255, 255, 255, 0.18);
  overflow: hidden;
}

.ci-mock__panel-progress-fill {
  height: 100%;
  width: 100%;
  background: var(--yfb-accent);
  animation: ci-countdown 5s linear infinite;
}

@keyframes ci-countdown {
  from { width: 100%; }
  to { width: 0%; }
}

@media (prefers-reduced-motion: reduce) {
  .ci-mock__panel-progress-fill {
    animation: none;
    width: 60%;
  }
}

/* 控制条 */
.ci-mock__controls {
  display: flex;
  align-items: center;
  gap: var(--yfb-sp-3);
}

.ci-mock__ctrl-label {
  font-family: var(--yfb-mono);
  font-size: 9px;
  font-weight: 700;
  color: var(--yfb-text-secondary);
  text-transform: uppercase;
  letter-spacing: var(--yfb-ls-mono);
  flex-shrink: 0;
}

.ci-mock__slider {
  flex: 1;
  accent-color: var(--yfb-accent);
}

.ci-mock__ctrl-value {
  font-family: var(--yfb-mono);
  font-size: 10px;
  font-weight: 700;
  color: var(--yfb-accent);
  flex-shrink: 0;
  min-width: 42px;
  text-align: right;
}

.ci-mock__hint {
  font-family: var(--yfb-mono);
  font-size: 11px;
  font-weight: 700;
  color: var(--yfb-text-secondary);
  text-align: center;
  text-transform: uppercase;
  letter-spacing: var(--yfb-ls-mono);
}

.ci-mock__footer {
  display: flex;
  align-items: center;
  gap: var(--yfb-sp-2);
  padding: var(--yfb-sp-2) var(--yfb-sp-4);
  background-color: var(--yfb-accent);
  color: var(--yfb-on-accent);
  font-family: var(--yfb-mono);
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: var(--yfb-ls-mono);
  border-top: 1px solid var(--yfb-rule);
  justify-content: center;
}
</style>
